/**
 * usePaginatorTemplate Hook
 * 
 * A custom React hook that generates pagination templates and components for PrimeReact DataTable.
 * Provides internationalized pagination controls with custom styling and navigation functionality.
 * 
 * Purpose:
 * - Creates customized pagination UI components for DataTable 
 * - Provides consistent pagination styling across the application
 * - Manages pagination state and navigation logic
 * 
 * Features: 
 * - Custom styled pagination buttons with active/inactive states
 * - Dynamic page number styling based on digit count (99+, 999+, etc.)
 * - Left template showing "Showing X to Y of Z records" information
 * - Navigation controls: First, Previous, Next, Last page buttons
 * - Accessibility support with proper alt texts and ARIA labels
 * - Responsive design with conditional styling
 * - Icon-based navigation with SVG assets
 *  
 * 
 * @param {Object} params - Hook parameters
 * @param {number} params.totalRecords - Total number of records in the dataset
 * @param {number} params.first - Index of the first record being displayed (0-based)
 * @param {number} params.rows - Number of rows displayed per page
 * 
 * @returns {Object} Pagination template object containing:
 * @returns {Function} paginatorLeftTemplate - Function that renders the record count display
 * @returns {Object} paginatorTemplate - Object containing PrimeReact pagination template configuration
 * @returns {string} paginatorTemplate.layout - Layout string defining pagination element order
 * @returns {Function} paginatorTemplate.FirstPageLink - Component for first page navigation
 * @returns {Function} paginatorTemplate.PrevPageLink - Component for previous page navigation
 * @returns {Function} paginatorTemplate.PageLinks - Component for individual page number links
 * @returns {Function} paginatorTemplate.NextPageLink - Component for next page navigation
 * @returns {Function} paginatorTemplate.LastPageLink - Component for last page Navigation
 * 
 * *
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PaginationButton, StyledCasesCountFooter, PageLink} from '../DataTable.styled';
import FirstPageIconActive from 'src/common/refactoredComponents/assets/images/FirstPageIconActive.svg';
import FirstPageIconInactive from 'src/common/refactoredComponents/assets/images/FirstPageIconInactive.svg';
import LastPageIconActive from 'src/common/refactoredComponents/assets/images/LastPageIconActive.svg';
import LastPageIconInactive from 'src/common/refactoredComponents/assets/images/LastPageIconInactive.svg';
import NextIconActive from 'src/common/refactoredComponents/assets/images/NextIconActive.svg';
import NextIconInactive from 'src/common/refactoredComponents/assets/images/NextIconInactive.svg';
import PreviousIconActive from 'src/common/refactoredComponents/assets/images/PreviousIconActive.svg';
import PreviousIconInactive from 'src/common/refactoredComponents/assets/images/PreviousIconInactive.svg';

const usePaginatorTemplate = ({ totalRecords, first, rows,paginationLeftMarginBottom,
    rightCOntentMarginTop, paginationInfoText, paginationInfoFormatter }) => {
    const { t } = useTranslation();
    const { isLanguageChanged } = useSelector((state) => state.loginReducer);

    const marginTopValue = `${rightCOntentMarginTop}px`;

    const paginatorLeftTemplate = () => {
        return isLanguageChanged ? (
            <StyledCasesCountFooter paginationLeftMarginBottom={paginationLeftMarginBottom}>
                {paginationInfoFormatter
                    ? paginationInfoFormatter(
                        totalRecords,
                        totalRecords > 0 ? first + 1 : 0,
                        first + rows > totalRecords ? totalRecords : first + rows
                    )
                    : `${totalRecords}  中  ${
                        totalRecords > 0 ? first + 1 : 0
                    }  から ${
                        first + rows > totalRecords ? totalRecords : first + rows
                    }  を表示`}
            </StyledCasesCountFooter>
        ) : (
            <StyledCasesCountFooter paginationLeftMarginBottom={paginationLeftMarginBottom}>
                {paginationInfoText ? paginationInfoText : t('pagination.showingCasesText')}{' '}
                {totalRecords > 0 ? first + 1 : 0} {t('pagination.toText')}{' '}
                {first + rows > totalRecords ? totalRecords : first + rows}{' '}
                {t('pagination.ofText')} {totalRecords}
            </StyledCasesCountFooter>
        );
    };

    const paginatorTemplate = {
        layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink',
        FirstPageLink: function FirstPageLink(options) {
            return ( 
                <PaginationButton
                position={'relative'}
                margin={'0 5px 0 0'}
                disabled={options.disabled}
                onClick={!options.disabled ? options.onClick : undefined}
                rightCOntentMarginTop={marginTopValue}
                >
                    <img
                        src={
                            options.disabled
                                ? FirstPageIconInactive
                                : FirstPageIconActive
                        }
                        alt="first page"
                        width="auto"
                        height="auto"
                    />
                </PaginationButton>
            );
        },

        PrevPageLink: function PrevPageLink(options) {
            return ( 
                <PaginationButton
                disabled={options.disabled}
                onClick={!options.disabled ? options.onClick : undefined}
                rightCOntentMarginTop={marginTopValue}
                >
                    <img
                        src={
                            options.disabled
                                ? PreviousIconInactive
                                : PreviousIconActive
                        }
                        alt="previous page"
                        width="auto"
                        height="auto"
                    />
                </PaginationButton>
            );
        },
 
        PageLinks: function PageLinks(options) {
            const pageNo = options.page + 1; // Page numbers are 0-indexed, so we add 1 for display
            const baseMargin = pageNo >= 1000 ? '1px' : pageNo > 99 ? '7px' : '5px';
            const selected = options.currentPage === options.page;
            return ( 
                <PageLink
                    id={`page-link-${pageNo}`}
                    onClick={options.onClick}  
                    marginLeft = {baseMargin}
                    marginRight = {baseMargin} 
                    width ={ pageNo >= 1000 ? '43px': pageNo >= 100? '30px' : '22px'} 
                    border = {selected ? '1px solid black' : ''}
                    background = {selected ? '#4F4F4F 0% 0% no-repeat padding-box' : ''}
                    opacity= {selected ? 1 : ''}
                    color= {selected ? 'white' : ''}
                    marginBottom= {'10px'}
                    rightCOntentMarginTop={selected ? (rightCOntentMarginTop-1)+'px': marginTopValue}
                    >
                    {options.page + 1}
                </PageLink>
            );
        },
        NextPageLink: function NextPageLink(options) {
            return ( 
                <PaginationButton
                disabled={options.disabled}
                onClick={!options.disabled ? options.onClick : undefined}
                rightCOntentMarginTop={marginTopValue}
                >
                    <img
                        src={
                            options.disabled ? NextIconInactive : NextIconActive
                        }
                        alt="next page"
                        width="auto"
                        height="auto"
                    />
                </PaginationButton>
            );
        },
        LastPageLink: function LastPageLink(options) {
            return ( 
                 <PaginationButton
                position={'relative'}
                margin={'0 0 0 5px'}
                disabled={options.disabled}
                onClick={!options.disabled ? options.onClick : undefined}
                rightCOntentMarginTop={marginTopValue}
                >
                    <img
                        src={
                            options.disabled
                                ? LastPageIconInactive
                                : LastPageIconActive
                        }
                        alt="last page"
                        width="auto"
                        height="auto"
                    />
                </PaginationButton>
            );
        },
    };

    return {
        paginatorLeftTemplate,
        paginatorTemplate
    };
};

export default usePaginatorTemplate;